import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plane, Loader2, Compass } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grain relative flex min-h-screen items-center justify-center overflow-hidden bg-[#070C18] px-4">
      <div className="pointer-events-none absolute -left-40 top-0 h-96 w-96 rounded-full bg-cyan-500/10 blur-3xl" />
      <div className="pointer-events-none absolute -right-40 bottom-0 h-96 w-96 rounded-full bg-blue-600/10 blur-3xl" />
      <div className="animate-fade-up relative z-10 w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 hud-glow">
            <Plane className="h-8 w-8 text-cyan-400" strokeWidth={1.5} />
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-50">
            VFR NAV ORGANIZER
          </h1>
          <p className="mt-2 flex items-center gap-2 text-sm text-slate-400">
            <Compass className="h-4 w-4 text-cyan-500" /> Console de préparation VFR
          </p>
        </div>

        <form onSubmit={submit} className="glass rounded-2xl p-8">
          <h2 className="mb-6 font-display text-xl font-semibold text-slate-100">Connexion</h2>
          {error && (
            <div data-testid="login-error" className="mb-4 rounded-lg border border-rose-500/30 bg-rose-500/10 px-4 py-2 text-sm text-rose-300">
              {error}
            </div>
          )}
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Email</Label>
              <Input data-testid="login-email-input" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} required
                className="mt-1.5 border-slate-700 bg-slate-900/60 text-slate-100" placeholder="pilote@exemple.fr" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Mot de passe</Label>
              <Input data-testid="login-password-input" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} required
                className="mt-1.5 border-slate-700 bg-slate-900/60 text-slate-100" placeholder="••••••••" />
            </div>
          </div>
          <Button data-testid="login-submit-button" type="submit" disabled={loading}
            className="mt-6 w-full bg-cyan-500 font-semibold text-[#070C18] hover:bg-cyan-400">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Se connecter"}
          </Button>
          <p className="mt-5 text-center text-sm text-slate-400">
            Pas encore de compte ?{" "}
            <Link data-testid="go-register-link" to="/register" className="text-cyan-400 hover:text-cyan-300">
              Créer un compte
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
